import * as React from 'react'
import { action, computed, observable, makeObservable } from 'mobx'
import { useOnEscDown } from '../hooks/key-press'

/**
 * Entry in the Modal Stack
 */
export class ModalStackEntry {
  /**
   * Backing close method.
   * @private
   */
  private _close?: () => unknown

  /**
   * Whether `pop`ing is allowed (used to block closing on Esc
   */
  @observable allowPop: boolean = true

  /**
   * Whether the modal is showing.
   */
  @observable showing: boolean = false

  /**
   * Constructor.
   *
   * @param manager
   */
  constructor(private manager: ModalStackManager) {
    makeObservable(this)
    this.allowPop = true
    this.showing = false
  }

  /**
   * Get the layer position for this entry.
   */
  @computed
  get layer() {
    return this.manager.getLayerForEntry(this)
  }

  /**
   * Calls the close handler.
   */
  @action.bound
  close() {
    this._close?.()
  }

  /**
   * Called popping from the stack. Closes if allowed.
   */
  @action.bound
  pop() {
    if (this.allowPop) {
      this.close()
    }
  }

  /**
   * Removes the entry.
   */
  remove() {
    this.manager.removeEntry(this)
  }

  /**
   * Updates the props.
   *
   * @param showing
   * @param close
   * @param allowPop
   */
  @action.bound
  update(
    showing: boolean,
    close: (() => unknown) | undefined,
    allowPop: boolean,
  ) {
    this.showing = showing
    this._close = close
    this.allowPop = allowPop
  }
}

/**
 * Used to manage the modal stack.
 */
export class ModalStackManager {
  @observable private stack: ModalStackEntry[] = []

  constructor() {
    makeObservable(this)
  }

  /**
   * Portion of the stack that is currently showing.
   */
  @computed
  get showingStack() {
    return this.stack.filter((x) => x.showing)
  }

  /**
   * Returns the layer (position in the stack) for the entry.
   * Starts at 1 to make it easier to compute things in the UI.
   * The top layer is 1.
   */
  getLayerForEntry(entry: ModalStackEntry) {
    const showing = this.showingStack
    const idx = showing.indexOf(entry)
    if (idx === -1) {
      return 0
    }
    return showing.length - idx
  }

  /**
   * Adds an entry.
   */
  @action.bound
  add() {
    const entry = new ModalStackEntry(this)
    this.stack.push(entry)
    return entry
  }

  /**
   * Removes the entry
   * @param entry
   */
  @action.bound
  removeEntry(entry: ModalStackEntry) {
    const idx = this.stack.indexOf(entry)
    if (idx > -1) {
      this.stack.splice(idx, 1)
    }
  }

  /**
   * Pop the top layer from the stack and closes it.
   */
  @action.bound
  pop() {
    const showing = this.showingStack
    showing[showing.length - 1]?.pop()
  }
}

// NOTE: This will always be set at the app root, hence the `!`.
const ModalStackContext = React.createContext<ModalStackManager>(undefined!)

/**
 * Should only be used by the `useModalStack` hook.
 */
export function useModalStackManager() {
  return React.useContext(ModalStackContext)
}

export function ModalStackProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [stackManager] = React.useState(() => new ModalStackManager())
  useOnEscDown(stackManager.pop)
  return (
    <ModalStackContext.Provider value={stackManager}>
      {children}
    </ModalStackContext.Provider>
  )
}
